/**
 * Timetable Assignments Service
 *
 * Manages the assignment of subjects, teachers, and classes to timetable slots.
 * Each assignment maps a class to a specific period on a specific cycle day.
 *
 * This is the critical link between:
 *   Timetable (which day template runs when) →
 *   Day Template (which periods exist) →
 *   Assignment (which class has which subject with which teacher in that period)
 *
 * Collection: schools/{schoolId}/timetableAssignments
 */

import {
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  deleteDoc,
  query,
  where,
  onSnapshot,
  writeBatch,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase';

// Cache
let cachedAssignments = null;
let cacheTimetableId = null;
let cacheSchoolId = null;
let cacheTimestamp = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

class TimetableAssignmentsService {
  constructor() {
    this.collectionName = 'timetableAssignments';
    this.listeners = new Map();
  }

  getCollectionRef(schoolId) {
    if (!schoolId) {
      throw new Error('School ID is required for timetable assignments');
    }
    return collection(db, 'schools', schoolId, this.collectionName);
  }

  clearCache() {
    cachedAssignments = null;
    cacheTimetableId = null;
    cacheSchoolId = null;
    cacheTimestamp = null;
  }

  /**
   * Get all assignments for a timetable (with caching)
   */
  async getAll(schoolId, timetableId) {
    if (!schoolId || !timetableId) {
      throw new Error('School ID and Timetable ID are required');
    }

    // Check cache
    if (cachedAssignments &&
        cacheSchoolId === schoolId &&
        cacheTimetableId === timetableId &&
        cacheTimestamp &&
        Date.now() - cacheTimestamp < CACHE_DURATION) {
      return cachedAssignments;
    }

    try {
      const collectionRef = this.getCollectionRef(schoolId);
      const q = query(collectionRef, where('timetableId', '==', timetableId));
      const snapshot = await getDocs(q);

      const assignments = [];
      snapshot.forEach((doc) => {
        assignments.push({ id: doc.id, ...doc.data() });
      });

      // Update cache
      cachedAssignments = assignments;
      cacheTimetableId = timetableId;
      cacheSchoolId = schoolId;
      cacheTimestamp = Date.now();

      return assignments;
    } catch (error) {
      console.error('Error fetching timetable assignments:', error);
      return [];
    }
  }

  /**
   * Get assignments for a specific class
   */
  async getForClass(schoolId, timetableId, classId) {
    const all = await this.getAll(schoolId, timetableId);
    return all.filter(a => a.classId === classId);
  }

  /**
   * Get assignments for a specific teacher
   */
  async getForTeacher(schoolId, timetableId, teacherId) {
    const all = await this.getAll(schoolId, timetableId);
    return all.filter(a => a.teacherId === teacherId);
  }

  /**
   * Get assignments for a specific cycle day
   */
  async getForCycleDay(schoolId, timetableId, cycleDayIndex) {
    const all = await this.getAll(schoolId, timetableId);
    return all.filter(a => a.cycleDayIndex === cycleDayIndex);
  }

  /**
   * Get a single assignment by slot key (cycleDayIndex-periodIndex-classId)
   */
  getBySlot(assignments, cycleDayIndex, periodIndex, classId) {
    return assignments.find(a =>
      a.cycleDayIndex === cycleDayIndex &&
      a.periodIndex === periodIndex &&
      a.classId === classId
    );
  }

  /**
   * Subscribe to real-time updates for a timetable's assignments
   */
  subscribe(schoolId, timetableId, callback) {
    if (!schoolId || !timetableId) {
      console.warn('Missing schoolId or timetableId for assignments subscription');
      callback([]);
      return () => {};
    }

    const key = `${schoolId}-${timetableId}`;
    this.unsubscribe(key);

    const collectionRef = this.getCollectionRef(schoolId);
    const q = query(collectionRef, where('timetableId', '==', timetableId));

    const unsubscribe = onSnapshot(q,
      (snapshot) => {
        const assignments = [];
        snapshot.forEach((doc) => {
          assignments.push({ id: doc.id, ...doc.data() });
        });

        // Update cache
        cachedAssignments = assignments;
        cacheTimetableId = timetableId;
        cacheSchoolId = schoolId;
        cacheTimestamp = Date.now();

        callback(assignments);
      },
      (error) => {
        console.error('Error in assignments subscription:', error);
        callback([]);
      }
    );

    this.listeners.set(key, unsubscribe);
    return unsubscribe;
  }

  unsubscribe(key) {
    const unsubscribe = this.listeners.get(key);
    if (unsubscribe) {
      unsubscribe();
      this.listeners.delete(key);
    }
  }

  /**
   * Create or update a single assignment
   */
  async save(schoolId, assignmentData) {
    if (!schoolId) {
      throw new Error('School ID is required');
    }

    const errors = this.validateAssignment(assignmentData);
    if (errors.length > 0) {
      throw new Error(`Validation failed: ${errors.join(', ')}`);
    }

    // Generate deterministic ID from slot key
    const { id, ...data } = assignmentData;
    const assignmentId = id || this.generateSlotId(data);

    if (!id) {
      data.createdAt = serverTimestamp();
    }
    data.updatedAt = serverTimestamp();

    const docRef = doc(this.getCollectionRef(schoolId), assignmentId);
    await setDoc(docRef, data, { merge: true });

    this.clearCache();
    return { id: assignmentId, ...data };
  }

  /**
   * Batch save multiple assignments (for bulk operations like copy day)
   */
  async saveBatch(schoolId, assignments) {
    if (!schoolId || !assignments?.length) return;

    const batch = writeBatch(db);
    const collectionRef = this.getCollectionRef(schoolId);

    assignments.forEach((assignment) => {
      const { id, ...data } = assignment;
      const assignmentId = id || this.generateSlotId(data);
      const docRef = doc(collectionRef, assignmentId);
      batch.set(docRef, {
        ...data,
        updatedAt: serverTimestamp()
      }, { merge: true });
    });

    await batch.commit();
    this.clearCache();
  }

  /**
   * Delete a single assignment
   */
  async delete(schoolId, assignmentId) {
    if (!schoolId || !assignmentId) {
      throw new Error('School ID and Assignment ID are required');
    }

    const docRef = doc(this.getCollectionRef(schoolId), assignmentId);
    await deleteDoc(docRef);
    this.clearCache();
  }

  /**
   * Delete assignment by slot (cycleDayIndex + periodIndex + classId)
   */
  async deleteBySlot(schoolId, timetableId, cycleDayIndex, periodIndex, classId) {
    const slotId = this.generateSlotId({ timetableId, cycleDayIndex, periodIndex, classId });
    return this.delete(schoolId, slotId);
  }

  /**
   * Delete all assignments for a timetable (clean slate)
   */
  async deleteAllForTimetable(schoolId, timetableId) {
    if (!schoolId || !timetableId) {
      throw new Error('School ID and Timetable ID are required');
    }

    const assignments = await this.getAll(schoolId, timetableId);
    if (assignments.length === 0) return;

    // Firestore batch limit is 500
    const batches = [];
    for (let i = 0; i < assignments.length; i += 450) {
      const batch = writeBatch(db);
      const chunk = assignments.slice(i, i + 450);
      chunk.forEach(a => {
        batch.delete(doc(this.getCollectionRef(schoolId), a.id));
      });
      batches.push(batch.commit());
    }

    await Promise.all(batches);
    this.clearCache();
  }

  /**
   * Copy all assignments from one cycle day to another
   */
  async copyCycleDay(schoolId, timetableId, fromDayIndex, toDayIndex) {
    const assignments = await this.getForCycleDay(schoolId, timetableId, fromDayIndex);
    if (assignments.length === 0) return;

    const newAssignments = assignments.map(a => ({
      ...a,
      id: undefined,
      cycleDayIndex: toDayIndex
    }));

    await this.saveBatch(schoolId, newAssignments);
  }

  /**
   * Check for conflicts in existing assignments
   * Returns array of conflict descriptions
   */
  checkConflicts(newAssignment, existingAssignments) {
    const conflicts = [];
    const sameSlot = existingAssignments.filter(a =>
      a.timetableId === newAssignment.timetableId &&
      a.cycleDayIndex === newAssignment.cycleDayIndex &&
      a.periodIndex === newAssignment.periodIndex
    );

    // Teacher double-booking
    if (newAssignment.teacherId) {
      const teacherConflict = sameSlot.find(a =>
        a.teacherId === newAssignment.teacherId &&
        a.classId !== newAssignment.classId
      );
      if (teacherConflict) {
        conflicts.push({
          type: 'teacher',
          message: `${newAssignment.teacherName || 'Teacher'} is already assigned to ${teacherConflict.className || 'another class'} in this slot`
        });
      }
    }

    // Room double-booking (if room is specified)
    if (newAssignment.room) {
      const roomConflict = sameSlot.find(a =>
        a.room === newAssignment.room &&
        a.classId !== newAssignment.classId
      );
      if (roomConflict) {
        conflicts.push({
          type: 'room',
          message: `${newAssignment.room} is already used by ${roomConflict.className || 'another class'} in this slot`
        });
      }
    }

    return conflicts;
  }

  /**
   * Get all conflicts across a timetable
   */
  async getAllConflicts(schoolId, timetableId) {
    const assignments = await this.getAll(schoolId, timetableId);
    const conflicts = [];

    // Group by slot (cycleDayIndex + periodIndex)
    const slotMap = {};
    assignments.forEach(a => {
      const key = `${a.cycleDayIndex}-${a.periodIndex}`;
      if (!slotMap[key]) slotMap[key] = [];
      slotMap[key].push(a);
    });

    // Check each slot for teacher/room conflicts
    Object.entries(slotMap).forEach(([slot, slotAssignments]) => {
      // Teacher conflicts
      const teacherMap = {};
      slotAssignments.forEach(a => {
        if (a.teacherId) {
          if (teacherMap[a.teacherId]) {
            conflicts.push({
              type: 'teacher',
              slot,
              assignments: [teacherMap[a.teacherId], a],
              message: `${a.teacherName || 'Teacher'} double-booked in slot Day ${a.cycleDayIndex + 1} Period ${a.periodIndex + 1}`
            });
          }
          teacherMap[a.teacherId] = a;
        }
      });

      // Room conflicts
      const roomMap = {};
      slotAssignments.forEach(a => {
        if (a.room) {
          if (roomMap[a.room]) {
            conflicts.push({
              type: 'room',
              slot,
              assignments: [roomMap[a.room], a],
              message: `${a.room} double-booked in slot Day ${a.cycleDayIndex + 1} Period ${a.periodIndex + 1}`
            });
          }
          roomMap[a.room] = a;
        }
      });
    });

    return conflicts;
  }

  /**
   * Validate assignment data
   */
  validateAssignment(assignment) {
    const errors = [];

    if (!assignment.timetableId) errors.push('Timetable ID is required');
    if (assignment.cycleDayIndex === undefined || assignment.cycleDayIndex === null) {
      errors.push('Cycle day index is required');
    }
    if (assignment.periodIndex === undefined || assignment.periodIndex === null) {
      errors.push('Period index is required');
    }
    if (!assignment.classId && !assignment.activityTypeId) errors.push('Class ID is required');
    if (!assignment.subjectId && !assignment.activityTypeId) {
      errors.push('Subject ID or Activity Type ID is required');
    }

    return errors;
  }

  /**
   * Generate deterministic slot ID from assignment data
   * Activities use a separate key scheme (no classId, keyed by teacherId instead)
   */
  generateSlotId({ timetableId, cycleDayIndex, periodIndex, classId, activityTypeId, teacherId }) {
    if (activityTypeId) {
      return `${timetableId}_d${cycleDayIndex}_p${periodIndex}_act_${teacherId || 'shared'}`;
    }
    return `${timetableId}_d${cycleDayIndex}_p${periodIndex}_${classId}`;
  }
}

export const timetableAssignmentsService = new TimetableAssignmentsService();
export default timetableAssignmentsService;
